/**
 * @file 试卷分页生命周期
 * @description 挂载后测高、填充分页块，完成前不标记 ready。
 *
 * @whenToUse 三套打印模板。
 * @whenNotToUse 已经分好页的静态展示。
 */

import { useEffect, useRef, useState } from 'react'
import type { PaperBlock } from '@/components/pages/handout-page/print/blocks.tsx'
import type { HandoutPaperModel } from '@/components/pages/handout-page/print/paper-data.ts'
import {
  PaperPaginator,
  type PaginateMode,
} from '@/components/pages/handout-page/print/paginate.ts'
import { toast } from '@/components/ui/toast.tsx'

/**
 * 按模板模式跑一遍分页。
 * @param mode 分页规则
 * @param model 试卷数据
 * @param fill 往分页器里写入模块
 */
export function usePaperLayout(
  mode: PaginateMode,
  model: HandoutPaperModel,
  fill: (pager: PaperPaginator, model: HandoutPaperModel) => Promise<void>,
  onReady?: (ready: boolean) => void,
) {
  const [pages, setPages] = useState<PaperBlock[][]>([[]])
  const [ready, setReady] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const containers = useRef<(HTMLElement | null)[]>([])

  useEffect(() => {
    let cancelled = false
    const pager = new PaperPaginator(
      mode,
      setPages,
      (index) => containers.current[index] ?? null,
      () => rootRef.current,
    )
    void (async () => {
      try {
        setReady(false)
        onReady?.(false)
        await pager.prepare()
        if (cancelled) return
        await fill(pager, model)
        if (cancelled) return
        pager.prune()
        setReady(true)
        onReady?.(true)
      } catch (error) {
        if (cancelled) return
        toast.danger(error instanceof Error ? error.message : '试卷分页失败')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [mode, model, fill, onReady])

  return { pages, ready, rootRef, containers }
}
